/**
 * Turns a change-log row's stored `summary` (audit meta) into something a
 * person can read: column names become the wording staff see on screen, vote
 * tallies become a sentence, and the checklist's "fields" + "accepted
 * suggestions" pair becomes one list with the auto-filled answers marked.
 */
import { DECISIONS, SECTIONS, VOTES } from "@/components/admissions-committee/checklist/sections";

export interface DetailItem {
  label: string;
  value?: string;
  list?: { text: string; badge?: string }[];
  note?: string;
}

const KEY_LABELS: Record<string, string> = {
  fields: "Answers changed",
  accepted_suggestions: "Filled in from CMT records",
  reason: "Reason",
  note: "Note",
  decision: "Decision",
  vote: "Vote",
  tally: "Votes",
  capacity: "Acting as",
  closed_early: "Closed before everyone voted",
  held_for_board: "Held for the Board",
  roster_size: "Committee members",
  recipients: "Reminded",
  skipped_cooldown: "Not reminded (reminded recently)",
  by_staff: "Sent by staff",
  notified: "Members notified",
  sent_by: "Referred by",
  previous_status: "Previous status",
  applicant_name: "Applicant",
  cleared_answers: "Answers cleared",
  deleted_votes_count: "Votes removed",
  document: "Document",
  surface: "Opened from",
  added: "Added",
  title: "Event",
  new_status: "New status",
  status: "Status",
  role: "Role",
  assigned_user: "Person",
  target_user: "Person",
  before: "Before",
  after: "After",
  change_note: "Note",
  outcome: "Outcome",
  days_lapsed: "Days lapsed",
  tier: "Membership",
  member_name: "Member",
  rows: "Rows exported",
  truncated: "Cut off at the export limit",
  filters: "Filters",
};

/** Plumbing keys: shown in the developer view only. */
const TECHNICAL = new Set(["script", "application_id", "actions", "action", "assigned_user_id", "entity_id", "entity_type", "recipient_ids",
  "member_id", "outreach_id"]);

const CHOICE_LABELS: Record<string, string> = Object.fromEntries([...DECISIONS, ...VOTES].map(([k, v]) => [k, v]));

function humanize(key: string): string {
  const s = key.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const ROW_SECTION = new Map<string, { text: string; section: string }>();
for (const s of SECTIONS) {
  const section = s.title.includes(" · ") ? s.title.split(" · ").slice(1).join(" · ") : s.title;
  for (const r of s.rows) {
    ROW_SECTION.set(r.key, { text: r.text, section });
    if (r.comment) ROW_SECTION.set(r.comment, { text: `${r.text} (comment)`, section });
  }
}

/** A checklist column (or any other field name) in words. */
export function fieldLabel(key: string): string {
  const row = ROW_SECTION.get(key);
  if (row) return `${row.text.replace(/^or /, "")} — ${row.section}`;
  const base = key.replace(/_(notes|comments?)$/, "");
  if (base !== key && ROW_SECTION.get(base)) return `${ROW_SECTION.get(base)!.text} (comment)`;
  return humanize(key);
}

function scalar(key: string, v: unknown): string {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return CHOICE_LABELS[v] ?? (key === "capacity" ? humanize(v) : v);
  if (typeof v === "number") return String(v);
  return JSON.stringify(v);
}

export function describeSummary(summary: Record<string, unknown> | null | undefined, developer = false): DetailItem[] {
  if (!summary) return [];
  const out: DetailItem[] = [];
  const accepted = Array.isArray(summary.accepted_suggestions) ? (summary.accepted_suggestions as string[]) : [];

  for (const [key, value] of Object.entries(summary)) {
    if (!developer && TECHNICAL.has(key)) continue;
    const label = KEY_LABELS[key] ?? humanize(key);

    if (key === "fields" && Array.isArray(value)) {
      const fields = value as string[];
      out.push({
        label: `${label} (${fields.length})`,
        list: fields.map((f) => ({ text: fieldLabel(f), badge: accepted.includes(f) ? "From CMT records" : undefined })),
        note: accepted.length ? `${accepted.length} of ${fields.length} filled in automatically from CMT records.` : undefined,
      });
      continue;
    }
    if (key === "accepted_suggestions" && Array.isArray(summary.fields)) continue;   // folded into "fields"

    if (key === "tally" && value && typeof value === "object") {
      const t = value as Record<string, number>;
      const parts = ["approve", "defer", "deny"].filter((k) => k in t).map((k) => `${t[k]} ${CHOICE_LABELS[k]?.toLowerCase() ?? k}`);
      out.push({ label, value: `${parts.join(" · ")}${t.total !== undefined ? ` (${t.total} cast)` : ""}` });
      continue;
    }
    if (Array.isArray(value)) {
      if (!value.length) continue;
      out.push({ label, list: value.map((v) => ({ text: typeof v === "string" ? (ROW_SECTION.has(v) ? fieldLabel(v) : v) : JSON.stringify(v) })) });
      continue;
    }
    if (value && typeof value === "object") {
      out.push({ label, list: Object.entries(value as Record<string, unknown>).map(([k, v]) => ({ text: `${fieldLabel(k)}: ${scalar(k, v)}` })) });
      continue;
    }
    out.push({ label, value: scalar(key, value) });
  }
  return out;
}
